import queryString from 'query-string';
import { getStagingApiBaseUrl } from './url.utils';
import { Environments, CXEnvironments } from './types';
/**
 * Dev-only port-cluster offset for running multiple parallel dev
 * environments (git worktrees). The value is injected at bundle time by
 * DefinePlugin (`process.env.HB_PORT_OFFSET`) from the building env's
 * HB_CLUSTER_INDEX — see the Node-side sibling implementation in
 * `@honeybook/config-apps/port-offset`. Returns 0 in production builds,
 * tests, and any non-bundled context.
 */
export function getDevPortOffset() {
    try {
        const offset = Number(process.env.HB_PORT_OFFSET);
        // Number.isFinite rejects NaN (unset / non-numeric define) explicitly
        // instead of masking it behind `|| 0`.
        return Number.isFinite(offset) ? offset : 0;
    }
    catch (e) {
        return 0; // non-bundled context — process is not defined
    }
}
const ROUTER_BASE_PORT = 7100;
export const LOCAL_DEV_HOST = `localhost:${ROUTER_BASE_PORT + getDevPortOffset()}`;
/**
 * Absolute local backend origin for code that can't go through the router's
 * /api proxy (e.g. CX pages on a company subdomain). Follows the per-cluster
 * backend override (`HB_API_PROXY_TARGET`, injected dev-only by DefinePlugin —
 * same contract as the router proxy target); the shared default backend
 * otherwise.
 */
export function getLocalApiBaseUrl() {
    try {
        if (process.env.HB_API_PROXY_TARGET) {
            return `${process.env.HB_API_PROXY_TARGET}/`;
        }
    }
    catch (e) {
        // non-bundled context — process is not defined
    }
    return 'http://localhost:8000/';
}
// HB_DEV_SERVER proxy — shared infrastructure, never offset
export const DEV_SERVER_HOST = 'localhost:3030';
// A local dev router origin (http://localhost:7100–7199) — the only form the
// shared local backend ever reports for the react origin.
const LOCAL_ROUTER_ORIGIN_PATTERN = /^(https?:\/\/)localhost:71\d{2}(?=[/?#]|$)/;
/**
 * The local backend (glue) is shared by every dev port cluster and always
 * reports the default cluster's react origin (`http://localhost:7100/`) in
 * the gon configuration's `url` / `app_url`. A bundle running on a non-zero
 * cluster (`HB_CLUSTER_INDEX` > 0) would build API requests and redirects
 * from those values and target the wrong cluster. Rewrites any local router
 * origin in those fields to this bundle's own cluster origin
 * (`LOCAL_DEV_HOST`). Staging and production URLs never match the local
 * router pattern, so outside cluster dev the config is returned unchanged.
 */
export function amendConfigUrlsForDevCluster(config) {
    const ownClusterOrigin = `$1${LOCAL_DEV_HOST}`;
    const amendedUrl = config.url?.replace(LOCAL_ROUTER_ORIGIN_PATTERN, ownClusterOrigin);
    const amendedAppUrl = config.app_url?.replace(LOCAL_ROUTER_ORIGIN_PATTERN, ownClusterOrigin);
    if (amendedUrl === config.url && amendedAppUrl === config.app_url) {
        return config;
    }
    return { ...config, url: amendedUrl, app_url: amendedAppUrl };
}
export function isProduction() {
    return process.env.NODE_ENV === 'production';
}
export function isDevelopment() {
    return process.env.NODE_ENV === 'development';
}
export function isTesting() {
    return process.env.NODE_ENV === 'test';
}
export var EnvTypes;
(function (EnvTypes) {
    EnvTypes["Staging"] = "staging";
    EnvTypes["Production"] = "production";
    EnvTypes["Local"] = "local";
    EnvTypes["RemoteDev"] = "remoteDev";
})(EnvTypes || (EnvTypes = {}));
/**
 * Get the current environment based on the URL
 * Supports query parameters: cpu, cpd, cpp
 * Persists CPU to session storage
 */
export function getEnvironment() {
    const { hostname } = new URL(window.location.href);
    let prefix, domain;
    const parts = hostname.split('.');
    if (parts.length > 1) {
        prefix = parts.shift();
        domain = parts.join('.');
    }
    else {
        domain = parts.shift();
    }
    return calculateEnvironment(domain, prefix);
}
export function getApiBaseUrl() {
    const env = getEnvironment();
    if (!env) {
        return '';
    }
    switch (env.environment) {
        case Environments.Production:
            return 'https://api.honeybook.com';
        case Environments.Development:
            return '';
        case Environments.Staging:
            return getStagingApiBaseUrl();
        default:
            return '';
    }
}
/**
 * Calculate the CX environment based on domain and prefix
 */
export function calculateEnvironment(domain, prefix) {
    const environment = getEnvironmentByDomain(domain, prefix);
    return {
        domain,
        prefix,
        environment
    };
}
/**
 * Determine environment based on domain and prefix
 */
function getEnvironmentByDomain(domain, prefix) {
    if (prefix === 'app' && domain === 'hbqa.dev') {
        return Environments.Staging;
    }
    // e2e env
    if (/^hb-app-staging-.+$/.test(domain) ||
        /^hb-mx-staging-.+$/.test(domain) ||
        /^hb-cx-staging-.+$/.test(domain)) {
        return Environments.Staging;
    }
    switch (domain) {
        case 'hbportal.co':
            return Environments.Production;
        case 'web.e2e.stg-us-east-1-v7.hbdev.io':
        case 'web.preview.stg-us-east-1-v7.hbdev.io':
        case 'api.e2e.stg-us-east-1-v7.hbdev.io':
        case 'api.preview.stg-us-east-1-v7.hbdev.io':
        case 'ws.e2e.stg-us-east-1-v7.hbdev.io':
        case 'ws.preview.stg-us-east-1-v7.hbdev.io':
        case 'e2e.hbdev.io':
        case 'stg.hbdev.io':
        case 'hb-site-dev.com':
            return Environments.Staging;
        case 'localhost':
        case 'localhost:3000':
        case 'lvh.me':
        case 'ngrok-free.app':
        case 'ngrok.app':
            return Environments.Development;
        default:
            return getEnvironmentFromUrl();
    }
}
/**
 * Get environment from URL query parameter
 */
function getEnvironmentFromUrl() {
    const { env } = queryString.parse(window.location.search);
    return env === Environments.Staging
        ? Environments.Staging
        : Environments.Production;
}
export const getIsInIframe = () => {
    return window.self !== window.top;
};
export function isHbDevServer() {
    try {
        return process.env.HB_DEV_SERVER === 'true';
    }
    catch (e) {
        return false;
    }
}
export function getStgEnvFromEnvVar() {
    try {
        if (process.env.STG_ENV) {
            return process.env.STG_ENV;
        }
    }
    catch (e) {
        // Missing env var.
    }
}
export function getStgEnvValue() {
    const stgEnvFromEnvVar = getStgEnvFromEnvVar();
    try {
        const hostname = window.location.hostname;
        if (hostname.includes('hb-mx-staging-') ||
            hostname.includes('hb-app-staging-') ||
            hostname.includes('hb-cx-staging-')) {
            const match = hostname.match(/hb-(mx|app|cx)-staging-([^.]+)\.web\.([^.]+)\.stg/);
            return match
                ? {
                    envName: stgEnvFromEnvVar || match[2],
                    envType: match[3]
                }
                : null;
        }
        else if (hostname === 'app.hbqa.dev') {
            return {
                envName: 'qa',
                envType: 'preview'
            };
        }
        else if (stgEnvFromEnvVar) {
            return {
                envName: stgEnvFromEnvVar,
                envType: 'preview'
            };
        }
    }
    catch (e) {
        // Failed to match the hostname.
    }
    return null;
}
export function getLegacyAppBaseUrl() {
    // For staging environments
    const stgValue = getStgEnvValue();
    if (stgValue) {
        if (stgValue.envName === 'qa') {
            return 'https://www.hbqa.dev';
        }
        return `https://hb-app-staging-${stgValue.envName}.web.${stgValue.envType}.stg-us-east-1-v7.hbdev.io`;
    }
    // if we're on localhost (or a .localhost subdomain, e.g. company.localhost used by CP MFE dev)
    // use local server
    if (window.location.hostname === 'localhost' ||
        window.location.hostname.endsWith('.localhost')) {
        return isHbDevServer()
            ? `http://${DEV_SERVER_HOST}`
            : 'http://localhost:3000';
    }
    // Default production URL
    return 'https://www.honeybook.com';
}
// --- CX (client-experience) environment resolution ----------------------
// Colocated with the general environment resolver above. This CX variant adds
// `awsBucket` derivation, `cpu`/`cpd`/`cpp` impersonation (+ sessionStorage),
// a `LOCAL_DEV_HOST` development case the general `getEnvironment` lacks, and
// pre-resolves the S3 `configDomain` (ngrok/localhost swap) + `filePrefix`
// (e2e nonce) so downstream consumers touch no globals. Kept alongside the
// general resolver on purpose — the two should be unified when this
// (anti-pattern) package is dissolved. Moved from `src/utils/cx-utils/`.
const E2E_NONCE_KEY = 'e2e_nonce';
export function getCXEnvironment(logger) {
    const { hostname, searchParams } = new URL(window.location.href);
    let hostnameToUse = hostname;
    let prefix;
    let domain;
    if (searchParams.has('cpu')) {
        hostnameToUse = searchParams.get('cpu');
        persistCpuToSessionStorage(hostnameToUse, logger);
    }
    else if (searchParams.has('cpd') && searchParams.has('cpp')) {
        prefix = searchParams.get('cpp');
        domain = searchParams.get('cpd');
    }
    const existingCpu = fetchCpuFromSessionStorage(logger);
    if (existingCpu) {
        hostnameToUse = existingCpu;
    }
    if (!prefix || !domain) {
        const parts = hostnameToUse.split('.');
        if (parts.length > 1) {
            prefix = parts.shift();
            domain = parts.join('.');
        }
        else {
            domain = parts.shift();
        }
    }
    return calculateCXEnvironment(domain, prefix, getE2eNonce(logger));
}
// Pure given (domain, prefix, e2eNonce) — the caller reads the nonce from the
// URL/localStorage so this stays testable without touching globals.
export function calculateCXEnvironment(domain, prefix, e2eNonce) {
    const environment = getCXEnvironmentByDomain(domain, prefix);
    return {
        domain,
        prefix,
        environment,
        awsBucket: environment === CXEnvironments.Production
            ? 'hb-client-portal'
            : 'hb-staging-us-east-1-hb-client-portal-dev',
        configDomain: getCXConfigDomain(domain, environment),
        filePrefix: e2eNonce ? `${prefix}_${e2eNonce}` : prefix
    };
}
// The domain segment of the company config S3 key. In development, `domain` is
// just the raw hostname split (e.g. `localhost`) — not a real publish target —
// so configs are read from the shared `LOCAL_DEV_HOST` directory. ngrok tunnels
// are exempt: each is a real, unique public host a dev publishes under.
function getCXConfigDomain(domain, environment) {
    if (environment !== CXEnvironments.Development) {
        return domain;
    }
    return domain.indexOf('ngrok') > -1 ? domain : LOCAL_DEV_HOST;
}
function getCXEnvironmentByDomain(domain, prefix) {
    if (prefix === 'app' && domain === 'hbqa.dev') {
        return CXEnvironments.Staging;
    }
    // e2e env
    if (/^hb-app-staging-.+$/.test(domain) ||
        /^hb-mx-staging-.+$/.test(domain) ||
        /^hb-cx-staging-.+$/.test(domain)) {
        return CXEnvironments.Staging;
    }
    switch (domain) {
        case 'hbportal.co':
            return CXEnvironments.Production;
        case 'web.e2e.stg-us-east-1-v7.hbdev.io':
        case 'web.preview.stg-us-east-1-v7.hbdev.io':
        case 'api.e2e.stg-us-east-1-v7.hbdev.io':
        case 'api.preview.stg-us-east-1-v7.hbdev.io':
        case 'ws.e2e.stg-us-east-1-v7.hbdev.io':
        case 'ws.preview.stg-us-east-1-v7.hbdev.io':
        case 'e2e.hbdev.io':
        case 'stg.hbdev.io':
        case 'hb-site-dev.com':
            return CXEnvironments.Staging;
        case 'localhost':
        case 'localhost:3000':
        case LOCAL_DEV_HOST:
        case 'lvh.me':
        case 'ngrok-free.app':
        case 'ngrok.app':
            return CXEnvironments.Development;
        default:
            return getCXEnvironmentFromUrl();
    }
}
function getCXEnvironmentFromUrl() {
    const { env } = queryString.parse(window.location.search);
    return env === CXEnvironments.Staging
        ? CXEnvironments.Staging
        : CXEnvironments.Production;
}
// e2e runs publish branding under a nonce-suffixed file so parallel runs don't
// clobber each other. The nonce arrives via query param and is cached in
// localStorage for the rest of the session.
function getE2eNonce(logger) {
    const fromUrl = new URLSearchParams(window.location.search).get(E2E_NONCE_KEY);
    if (fromUrl) {
        try {
            localStorage.setItem(E2E_NONCE_KEY, fromUrl);
        }
        catch (error) {
            reportStorageError(logger, error, 'persist e2e nonce');
        }
        return fromUrl;
    }
    try {
        return localStorage.getItem(E2E_NONCE_KEY);
    }
    catch (error) {
        reportStorageError(logger, error, 'read e2e nonce');
    }
    return null;
}
function persistCpuToSessionStorage(cpu, logger) {
    try {
        sessionStorage.setItem('cpu', cpu);
    }
    catch (error) {
        reportStorageError(logger, error, 'persist cpu');
    }
}
function fetchCpuFromSessionStorage(logger) {
    try {
        return sessionStorage.getItem('cpu');
    }
    catch (error) {
        reportStorageError(logger, error, 'read cpu');
    }
    return null;
}
// A browser that denies storage (third-party iframe, sandbox) throws on access.
// The page carries on without storage; the denial is still reported.
function reportStorageError(logger, error, operation) {
    logger.error(new Error(`Failed to ${operation} in browser storage`, {
        cause: error
    }), { operation });
}
export function getEnvType() {
    if (window.location?.href?.includes('localhost')) {
        return EnvTypes.Local;
    }
    if (window.location?.href?.includes('hb-app-staging-devenv')) {
        return EnvTypes.RemoteDev;
    }
    if (window.location?.href?.includes('hb-app-staging') ||
        window.location?.href?.includes('staging-qa-app') ||
        window.location?.href?.includes('hb-mx-staging') ||
        window.location?.href?.includes('hbqa.dev')) {
        return EnvTypes.Staging;
    }
    return EnvTypes.Production;
}
//# sourceMappingURL=environment.utils.js.map